import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/jfbpb7og_Solutions%20Logo%20%282%29.png" 
              alt="Ability Healthcare Solutions" 
              className="h-12 w-auto"
              style={{ mixBlendMode: 'screen' }}
            />
            <div className="text-left">
              <div className="text-sm uppercase tracking-wider text-gray-300 font-bold">Turning Challenges</div>
              <div className="text-sm uppercase tracking-wider text-blue-400 font-bold">Into Solutions</div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        
        <div className="prose prose-invert prose-lg max-w-none space-y-6">
          <p className="text-gray-300">
            At Ability Healthcare Solutions, we take the privacy of our clients and website visitors very seriously. This privacy policy outlines the types of personal information we collect, how we use it, and the measures we take to protect your privacy.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Information We Collect</h2>
          <p className="text-gray-300">
            We collect personal information from clients and applicants, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Company/business information</li>
            <li>Service preferences and consultation details</li>
            <li>Payment information (processed securely through third-party providers)</li>
          </ul>
          <p className="text-gray-300">
            This information is collected to provide consultation services, communicate with clients, and support business operations.
          </p>
          <p className="text-gray-300">
            We may also collect non-personal information, such as website usage data and analytics, to improve user experience and site functionality.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">How We Use the Information</h2>
          <p className="text-gray-300">We use personal information to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Deliver and manage consulting services</li>
            <li>Communicate with clients regarding appointments, scheduling, and service delivery</li>
            <li>Send service updates, educational content, and promotional offers</li>
            <li>Process payments and manage financial transactions</li>
            <li>Respond to inquiries and provide customer support</li>
            <li>Comply with applicable state and federal regulations</li>
          </ul>
          <p className="text-gray-300">
            We do not sell or share personal information with third parties, except as required by law or as necessary to provide services to our clients and operate our business effectively.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">SMS and Email Communication Consent</h2>
          <p className="text-gray-300">
            By providing your phone number and email address, you consent to receive communications from Ability Healthcare Solutions via SMS text messages and email. These communications may include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Appointment confirmations and reminders</li>
            <li>Scheduling links (Calendly)</li>
            <li>Service updates and follow-ups</li>
            <li>Educational content about home care agency development</li>
            <li>Promotional offers and marketing materials</li>
            <li>Important business announcements</li>
          </ul>
          <p className="text-gray-300">
            Message and data rates may apply. Message frequency varies based on your service selection and engagement. You may opt out of SMS communications at any time by replying "STOP" to any message or contacting us directly at support@abilityhealthsolutions.com. You may unsubscribe from email communications by clicking the unsubscribe link in any email or contacting us directly.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Payment Processing</h2>
          <p className="text-gray-300">
            All payments are processed through secure third-party payment providers. We do not store credit card information on our servers. Payment information is transmitted directly to our payment processor using industry-standard encryption.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Data Security</h2>
          <p className="text-gray-300">
            We take appropriate security measures to protect the personal information collected by Ability Healthcare Solutions. This includes secure servers, data encryption, access restrictions, and physical safeguards to prevent unauthorized access, disclosure, alteration, or destruction of your information.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Third-Party Websites</h2>
          <p className="text-gray-300">
            Our website may contain links to third-party websites, including Calendly for scheduling and third-party services for payment processing. We are not responsible for the privacy practices or content of those sites and recommend that you review their privacy policies separately.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Cookies</h2>
          <p className="text-gray-300">
            The website uses cookies to help personalize your online experience. By accessing Ability Healthcare Solutions, you agree to use the required cookies. Cookies help us understand how visitors interact with our website and improve user experience.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Changes to This Policy</h2>
          <p className="text-gray-300">
            Ability Healthcare Solutions reserves the right to modify this privacy policy at any time. Any significant changes will be posted on our website. Continued use of our services after changes indicates acceptance of the updated policy.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-300">
            If you have questions or concerns about our privacy practices, please contact us:
          </p>
          <p className="text-gray-300">
            📧 support@abilityhealthsolutions.com<br/>
            🌐 www.abilityhealthsolutions.com
          </p>

          <p className="text-sm text-gray-500 mt-8">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Ability Healthcare Solutions. All rights reserved.
          </p>
          <div className="mt-4 space-x-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms & Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
